import {G} from "@svgdotjs/svg.js";
import {supportsStatistics} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/helpers";
import formatNumber from "@core/Util/format-number";
import {makeBezier2, makeLine} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/curves";
import {getNodeColor} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/helpers";
import {NODE_DIMENSIONS, LEGEND_DIMENSIONS} from "./constants";
import {isTagNode} from "./helpers";

/**
 * Calculate the center of the given points
 *
 * @param {JourneyBuilder.Sankey.ConnectionPoints} points Array of points, each point is an array with an X at pos 0, and a Y at pos 1
 * @return {{x: number, y: number}}
 */
function calculateCenterCoordinates(points){
    const { min, max } =  [points.topRight, points.bottomRight, points.topLeft, points.bottomLeft].reduce((prev, [x, y]) => {
        prev.min.x = Math.min(prev.min.x, x);
        prev.max.x = Math.max(prev.max.x, x);
        prev.min.y = Math.min(prev.min.y, y);
        prev.max.y = Math.max(prev.max.y, y);

        return prev;
    }, {
        max: { x:-Infinity, y: -Infinity },
        min: { x: Infinity, y: Infinity }
    });

    return {
        x: min.x + Math.abs(max.x - min.x) / 2,
        y: min.y + Math.abs(max.y - min.y) / 2
    }
}

/**
 * Create SVG path instructions for drawing a curved line between the given points
 *
 * @param {JourneyBuilder.Sankey.ConnectionPoints} points
 * @return {string}
 */
function createBezierCurvePathFromPositions(points){
    let isFirst = true;

    let mpp = (pathGenFn, pathPoints) => {
        let data = pathGenFn(pathPoints)
        if(!isFirst) {
            // remove the move command as we start where the previous command ended
            data = data.substr(data.indexOf(' ', 2) + 1)
        }

        isFirst = false;
        return data;
    }
    
    function generatePathCommandWithoutMiddlePoint() {
        isFirst = true;
        
        return [
            mpp(makeBezier2,[points.topLeft, points.topRight]),
            mpp(makeLine,[points.topRight, points.bottomRight]),
            mpp(makeBezier2,[points.bottomRight, points.bottomLeft]),
            'z' // close path
        ];
    }
    
    function generatePathCommandWithMiddlePoint() {
        isFirst = true;

        return [
            mpp(makeBezier2, [ points.topLeft, points.topMiddle ]),

            mpp(makeBezier2, [ points.topMiddle, points.topRight ] ),
            mpp(makeLine, [ points.topRight, points.bottomRight ] ),
            mpp(makeBezier2, [ points.bottomRight, points.bottomMiddle ]),

            mpp(makeBezier2, [ points.bottomMiddle, points.bottomLeft ] ),
            'z' // close path
        ]
    }
    
    const hasMiddlePoint = points.topMiddle && points.bottomMiddle;
    
    const pathCommand = hasMiddlePoint ? 
      generatePathCommandWithMiddlePoint() : 
      generatePathCommandWithoutMiddlePoint();

    // USE path instead of a  polygon for more advanced shapes
    return pathCommand.join(' ');
}

/**
 * Calculate the points required for creating a bezier curve between the given locations, with the destination being a tag node
 * 
 * @param {JourneyBuilder.Sankey.PositioningInfo} from
 * @param {JourneyBuilder.Sankey.PositioningInfo} to
 * @param {Number} individualConnectionHeight
 * @return {JourneyBuilder.Sankey.ConnectionPoints}
 */
function calculateTagBranchBezierPoints(from, to, individualConnectionHeight) {
    const { 
        x: maxIncomingX,
        y: maxIncomingY
    } = to.incomingNodeMaxCoordinates;
    
    let joinY = maxIncomingY;
    let joinX = maxIncomingX;
    
    //Attempt to calculate a joiner point for all incoming connections for the given tag brick
    if(to.y < joinY) {
        joinY -= Math.abs(to.y - joinY) / 2;
    } else {
        joinY += Math.abs(to.y - joinY) / 2;
    }

    joinX += (to.x - joinX) / 2.5;
    
    return {
        topLeft: [from.x + from.width, from.y],
        bottomLeft: [from.x + from.width, from.y + individualConnectionHeight],
        topRight: [to.x, to.y],
        bottomRight: [to.x, to.y + to.connectionHeight],

        //Include a single point where all connections join
        topMiddle: [ joinX, joinY ],
        bottomMiddle: [ joinX, joinY + individualConnectionHeight ]
    }
}

/**
 * Calculate the points required for creating a bezier curve between the given locations
 *
 * @param {JourneyBuilder.Sankey.PositioningInfo} from
 * @param {JourneyBuilder.Sankey.PositioningInfo} to
 * @return {JourneyBuilder.Sankey.ConnectionPoints}
 */
function calculateBranchBezierPoints(from, to){
    return {
        topLeft: [from.x + from.width, from.y],
        bottomLeft: [from.x + from.width, from.y + to.connectionHeight],
        topRight: [to.x, to.y],
        bottomRight: [to.x, to.y + to.connectionHeight]
    }
}

/**
 * Apply a striped background to the given element
 * 
 * @param {SVG} svg
 * @param {G} g
 * @param {Rect} r
 */
function applyStripedBackground(svg, g, r){
    let stripeyBackground = svg.findOne('.striped-background');
    
    if(!stripeyBackground){
        stripeyBackground = svg.pattern(4, 4, add => {
            add.attr('patternUnits', 'userSpaceOnUse');
            add.path('M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2')
                .stroke({
                    color: '#000000',
                    width: 1
                });
        });
    }

    //SVG.js should generate an id for us; this should make it easier to have multiple charts on the same page
    g.add(r.clone().fill(`url(#${stripeyBackground.id()})`));
}

/**
 * Create an SVG G element for the given node using the given positionInfo
 *
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.SankeyNode} node
 * @param {Boolean} containsFilteredChildren
 * @param {JourneyBuilder.Sankey.SankeyNode} [parentNode]
 * @param {JourneyBuilder.Sankey.PositioningInfo} positionInfo
 * @returns {G}
 */
function createElementForRegularNode(svg, node, containsFilteredChildren, parentNode, positionInfo) {
    const { x, y, width, elementHeight } = positionInfo;
    
    var g = new G();
    var r = g.rect();
    r.size(width, elementHeight).move(x, y);

    if(containsFilteredChildren){
        applyStripedBackground(svg, g, r);
    }

    const nodeClasses = ['node', `node-${(node.type||'').toLowerCase()}`];
    
    if(!supportsStatistics(node)){
        nodeClasses.push('node-no-statistics');
    }

    //.stroke({ width: 1, color: 'black' });
    g.attr('data-nodetype', node.type || '')
        .attr('data-nodeid', node.id)
        .attr('data-brick-id', node.brickId)
        .attr('data-amount', node.total)
        .attr('class', nodeClasses.join(' '))

    g.text(function(add) {
        let label = formatNumber(node.total);
        
        if(parentNode) {
            let percentage = 0;

            if(parentNode.total !== 0) {
                percentage = Math.round(node.total / (parentNode.total / 100));
            }

            if(isNaN(percentage)) percentage = 0;
            label += ` (${percentage}%)`;
        }

        //console.log('parent: ', container.parent)
        add.move(x + width + 2, y + 5 + 0.5 * elementHeight);
        add.attr('class', 'node-label');

        let txt = (node.label || '') + ': ';
        add.tspan(txt).attr('class','name'); // .newLine()
        add.tspan('' + label).attr('class','value')
    });
    
    return g;
}

/**
 * @param {SVG} svg
 * @param {String} brickId
 * @param {Boolean} show
 */
function toggleConnectionToTagBrickDisplay(svg, brickId, show) {
    svg.find(`.node-link[data-tag][data-tobrick="${brickId}"]`).forEach(
      node => show ? node.show() : node.hide()
    );
}

/**
 * Render all outgoing connections from the given node
 * 
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {Map<String, JourneyBuilder.Sankey.PositioningInfo>} positionInfoPerNode
 * @param {JourneyBuilder.Sankey.SankeyNode} fromNode
 * @param {String[]} collapsedTagBrickIds
 */
function renderOutgoingNodeConnectionsForNode(
  svg, 
  tree, 
  positionInfoPerNode, 
  fromNode,
  collapsedTagBrickIds
) {
    const fromBranch = tree.getBranchByNodeId(fromNode.id);
    
    if(!fromBranch) {
        return;
    }

    //Assume our parentElement provides scrolling
    const maxVisibleX =  svg.node.parentElement.scrollLeft + svg.node.parentElement.offsetWidth - LEGEND_DIMENSIONS.TYPE_LEGEND_WIDTH;
    const minVisibleX = svg.node.parentElement.scrollLeft - LEGEND_DIMENSIONS.TYPE_LEGEND_WIDTH - NODE_DIMENSIONS.WIDTH;

    const parentPosition = positionInfoPerNode.get(fromNode.id);
    
    //TODO: Include connection Y in positioninfo
    let connectionYOffset = 0;
    
    //https://datanext.atlassian.net/browse/DPN-3355
    //Tag connections should always be rendered on top, create a copy to prevent modifying our tree
    const toBranches = [...fromBranch.children]
      .sort((left, right) => {
          if(isTagNode(left.node) === isTagNode(right.node)) {
              return 0;
          }
          
          return isTagNode(left.node) ? -1 : 1;
      });

    toBranches.forEach(toBranch => {
        const toNode = toBranch.node;

        //Tag nodes get assigned positioninfo by brickid instead of node id
        const toPosition = positionInfoPerNode.get(toNode.id) || positionInfoPerNode.get(toNode.brickId);
        
        let toY = toPosition.y;
        let toX = toPosition.x;
        
        //collapsed nodes don't have their connections rendered
        if(isTagNode(toNode)) {
            if(collapsedTagBrickIds.includes(toNode.brickId)) {
                return;
            }
            
            //Check if source node is actually in view - nodes not in view are skipped
            const isInView = parentPosition.x > minVisibleX && parentPosition.x < maxVisibleX;
            if(!isInView) {
                return;
            }
            
            //Tag nodes are tag shaped - connections should go to the center
            toY += (toPosition.elementHeight / 2);
            
            //Tag nodes are always rendered - ensure all tag connections always reach their tags respectively
            toX = maxVisibleX;
        }
        
        renderConnectionBetweenNodes(
          svg,
          tree,
          fromNode,
          {
              ...parentPosition,

              //Lines are stacked, our y offset starts at 0 -> gets increased with each new line
              y: parentPosition.y + connectionYOffset
          },
          toNode,
          {
              ...toPosition,
              y: toY,
              x: toX
          }
        );
        
        //positionInfo.connectionHeight is a static value for tag nodes - for regular 
        connectionYOffset += toPosition.connectionHeightsByNodeId[fromNode.id];
    });
}

/**
 * Render a connection line between node A and node B
 *
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {JourneyBuilder.Sankey.SankeyNode} fromNode
 * @param {JourneyBuilder.Sankey.PositioningInfo} fromPos
 * @param {JourneyBuilder.Sankey.SankeyNode} toNode
 * @param {JourneyBuilder.Sankey.PositioningInfo} toPos
 */
function renderConnectionBetweenNodes(svg, tree, fromNode,fromPos, toNode, toPos) {
    const points = isTagNode(toNode) ?
      calculateTagBranchBezierPoints(fromPos, toPos, toPos.connectionHeightsByNodeId[fromNode.id]) :
      calculateBranchBezierPoints(fromPos, toPos);

    const g = new G();

    //Our text is positioned based on it's own width/height, for those properties to be calculated we need access to the boundingBox
    //the boundingBox only becomes available after adding our element to the DOM
    g.addTo(svg);
    
    if(!isTagNode(toNode)) {
        addLinkPath(points);
    } else {
        //Tag links consist of 2 parts - a line for our individual node, and a 'merger' line where all lines come together
        //this merger line is rendered as a separate element to make it easier for us to assign behavior to that specific part of the link
        addLinkPath({
            topLeft: points.topLeft,
            topRight: points.topMiddle,
            bottomLeft: points.bottomLeft,
            bottomRight: points.bottomMiddle
        });

        addLinkPath({
            topLeft: points.topMiddle,
            topRight: points.topRight,
            bottomLeft: points.bottomMiddle,
            bottomRight: points.bottomRight
        }, 'merge-link');
    }

    //Make our connection identifiable/add metadata used for display purposes 
    g.attr('data-fromnode', fromNode.id)
      .attr('data-tonode', toNode.id)
      .attr('data-fromamount', fromNode.total || 0)
      .attr('data-toamount', toNode.total || 0)
      .attr('data-fromnodetype', fromNode.type)
      .attr('data-tobrick', toNode.brickId)
      .attr('data-tag', isTagNode(toNode) || null)
      .attr('class', 'node-link');

    const connectionColor = getNodeColor(toNode);

    if(connectionColor){
        g.fill(connectionColor)
          .attr('data-variation', toNode.variation)
          .addClass('variation-node-link');
    }

    const center = calculateCenterCoordinates(points);

    addLinkPathLabel(
        fromNode.label,
        fromNode.total === 0 ? 0 : Math.round(toNode.total / (fromNode.total / 100))
    );
    
    if(isTagNode(toNode)) {
        //Tag merge links have a label containing the total of all incoming bricks
        let toSum = 0;
        let fromSum = 0;
        const fromLabels = [];

        tree.getBranchGroupByBrickId(toNode.brickId).forEach(branch => {
            const fromBranch = branch.parent;

            toSum += branch.node.total;
            fromSum += fromBranch.node.total;

            if(fromBranch.node.label && !fromLabels.includes(fromBranch.node.label)) {
                fromLabels.push(fromBranch.node.label);
            }
        });
        
        addLinkPathLabel(
            fromLabels,
            fromSum === 0 ? 0 : Math.round(toSum / (fromSum / 100))
        ).addClass('path-label-merge-link');
    }
    
    function addLinkPath(points, className) {
        const calculateLeeway = (top, bottom) => {
            const baseLeeway = 5;
            const height = bottom[1] - top[1];
            
            //Decrease our leeway as our actual path (= hoverable) area increases
            return Math.min(
                Math.max(baseLeeway - height, 0), 
                baseLeeway
            );
        };
        
        const withTopLeeway = (top, bottom) => [top[0], top[1] - calculateLeeway(top, bottom)];
        const withBottomLeeway = (top, bottom) => [bottom[0], bottom[1] + calculateLeeway(top, bottom)];

        g.path(createBezierCurvePathFromPositions({
            topLeft: points.topLeft,
            topRight: points.topRight,
            bottomLeft: points.bottomLeft,
            bottomRight: points.bottomRight
        })).attr('class', className);

        //Create a second transparent path to give the user some leeway when attempting to hover over some of the thinner links
        g.path(createBezierCurvePathFromPositions({
            topLeft: withTopLeeway(points.topLeft, points.bottomLeft),
            topRight: withTopLeeway(points.topRight, points.bottomRight),

            bottomLeft: withBottomLeeway(points.topLeft, points.bottomLeft),
            bottomRight: withBottomLeeway(points.topRight, points.bottomRight)
        })).attr('fill', 'transparent')
           .attr('class', className);
    }

    function addLinkPathLabel(fromNodeLabels, percentage) {
        fromNodeLabels = Array.isArray(fromNodeLabels) ? fromNodeLabels : [fromNodeLabels];
        percentage = !isNaN(percentage) ? percentage : 0;

        const pathLabel = g.text(function(add) {
            //Newlines get rendered before our label - thus our first label should not get a newline to prevent
            //our spacing from becoming jank
            fromNodeLabels.forEach((label, index) => {
                const labelElement = add.tspan(label || '').attr('class','name');
                if(index !== 0) {
                    labelElement.newLine();
                }
            });

            add.tspan(`▼ `).newLine();
            add.tspan(`${percentage}%`).attr('class','value');
            add.tspan(toNode.label || '').attr('class','name').newLine();
        });

        //Add a temporary rendering class to force the element to actually be rendered in the DOM
        //this allows us to read its width/height
        pathLabel.attr('class', 'path-label rendering');

        const { width: textWidth, height: textHeight } = pathLabel.node.getBBox();
        
        //Hide element again
        pathLabel.attr('class', 'path-label');
        
        //Tag node connections are rendered based on displayed distance between node/tag
        //this distance may be smaller than the distance between regular nodes, resulting in a very small area being assigned to our label
        //this in combination with us using scroll based overflow-x may cause tag path labels to be cut off
        //to ensure our label is fully displayed, move it slightly to the left based on our tag position/label width
        //when it was to be partially rendered out of bounds
        let x = center.x - (textWidth / 2);
        if(isTagNode(toNode)) {
            x = Math.min(x, points.topRight[0] - textWidth);
        }

        //Ensure labels rendered on the far left are still visible and don't touch our nodes
        x = Math.max(x, NODE_DIMENSIONS.WIDTH + 5);
        
        pathLabel.move(
            x,
            center.y - (textHeight / 2)
        );
        
        return pathLabel;
    }
}

/**
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {Map<String, JourneyBuilder.Sankey.PositioningInfo>} positionInfoPerNode
 * @param {String[]} collapsedTagBrickIds
 */
function renderTouchPoints(
  svg,
  tree,
  positionInfoPerNode,
  collapsedTagBrickIds
) {
    const nodeElements = [];

    tree.forAllBranches(branch => {
        const node = branch.node;
        const containsFilteredChildren = branch.containsFilteredChildren;
        const parentNode = branch.parent && branch.parent.node;

        //Tag nodes get assigned positioninfo by brickid instead of node id
        let positionInfo = positionInfoPerNode.get(node.id) || positionInfoPerNode.get(node.brickId);

        //Tag nodes have their own special renderer - it needs to be possible to render
        if(!isTagNode(node)) {
            nodeElements.push(createElementForRegularNode(svg, node, containsFilteredChildren, parentNode, positionInfo));
        }

        renderOutgoingNodeConnectionsForNode(svg, tree, positionInfoPerNode, node, collapsedTagBrickIds);
    });

    //Hide all tag routes previously marked hidden
    collapsedTagBrickIds.forEach(brickId => toggleConnectionToTagBrickDisplay(svg, brickId, false));

    // all done, add the rectangles to the dom last, so they'll always be on top of the connections
    nodeElements.forEach(element => svg.add(element));
}

/**
 * Remove and rerender all connections from nodes connected to the given set of tag nodes
 * 
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {JourneyBuilder.Sankey.SankeyNode[]} tagNodes
 * @param {Map<String, JourneyBuilder.Sankey.PositioningInfo>} positionInfoPerNode
 * @param {String[]} collapsedTagBrickIds
 */
function renderConnectionsToTagNodes(
  svg,
  tree,
  tagNodes,
  positionInfoPerNode,
  collapsedTagBrickIds
) {
    //Prevent re-rendering the same set of connections multiple times by keeping track of already processed node ids
    const processedNodeIds = new Set();

    tagNodes.forEach(tagNode => {
        //When our view is collapsed, our tagNode.from may be hidden, resulting in another node being displayed as our tag parent
        const tagBranch = tree.getBranchByNodeId(tagNode.id);
        const fromNode = tagBranch.parent && tagBranch.parent.node;

        if(!fromNode || processedNodeIds.has(fromNode.id)) {
            return;
        }

        processedNodeIds.add(fromNode.id);
        
        //Remove all previous connections from the source node - these will be restacked upon rendering our connections again
        svg.find(`[data-fromnode="${fromNode.id}"]`).forEach(el => el.remove());
        
        renderOutgoingNodeConnectionsForNode(svg, tree, positionInfoPerNode, fromNode, collapsedTagBrickIds);
    });
}

/**
 * Render lines between the different types/around the chart itself
 * @param {SVG} svg
 * @param {Object.<String, Number>} typeBaseYPositions
 */
function renderRaster(svg, typeBaseYPositions){
    const containerElement = new G();
    
    const { width: chartWidth, height: chartHeight } = svg.node.getBoundingClientRect();

    const drawVerticalLine =   (xPosition, strokeOptions) => drawLine(xPosition, 0, xPosition, chartHeight, strokeOptions);
    const drawHorizontalLine = (yPosition, strokeOptions) => drawLine(0, yPosition, chartWidth, yPosition, strokeOptions);

    const drawLine = (startX, startY, endX , endY, strokeOptions) => {
        containerElement.line(
            startX,
            startY,
            endX,
            endY
        ).stroke(strokeOptions);
    }

    //Draw a box around around our element
    drawVerticalLine(chartWidth - 1, {width: 1, color: '#E0E0E0'});
    drawHorizontalLine(chartHeight, {width: 1, color: '#E0E0E0'});

    drawVerticalLine(1, {width: 1, color: '#E0E0E0'});
    drawHorizontalLine(1, {width: 1, color: '#E0E0E0'});
    
    Object.values(typeBaseYPositions).forEach(y => {
        //Skip top most element to avoid overlap with box line which might make it appear a tad thicker
        if(y > 1){
            drawHorizontalLine(y, { color: '#F0F0F0', width: 0.75 });
        }
    })

    containerElement.addTo(svg);
}

export {
    renderTouchPoints,
    renderConnectionsToTagNodes,
    renderRaster
};